import { formatDateTime } from "../../lib/datetime.ts";
import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { StatusPill, type PillTone } from "../../auth/Pills.tsx";
import { Modal } from "../../auth/Modal.tsx";
import { ApiError } from "../../lib/api.ts";
import { COUNTRIES, subdivisionsFor } from "../../lib/geo.ts";
import { validateEmail, validateUrl, validateOptionalText, validateRequiredText, formatWebsiteSmart, INDUSTRY_CATEGORIES } from "../../lib/validate.ts";
import {
  formatPhoneForDisplay,
  normalizePhoneForStorage,
  validatePhoneForCountry,
  examplePhonePlaceholder,
} from "../../lib/phone.ts";
import {
  applyForOrgVerification,
  cancelOrgVerification,
  getOrganization,
  registerOrgCredential,
  updateOrganization,
  ORG_KEY,
  type OrgAssurance,
  type OrgEntityType,
  type OrgVerificationStatus,
  type Organization,
  type VerificationFieldLine,
  type VerificationDebug,
  type VerificationCorroboration,
} from "../../lib/management-client.ts";

/**
 * The ORGANIZATION settings area (Team screen, admin). Registration profile + verification status,
 * kept separate. Country is a dropdown; the state/province control is a dependent dropdown for
 * countries with a built-in subdivision list and a free-text field otherwise (jurisdiction-agnostic).
 * Website/email/phone are validated inline; save is blocked while any field is invalid. A pending
 * verification can be cancelled so the admin can edit and re-apply.
 */

/**
 * The registration-number FIELD LABEL for each entity type — the official document number differs by
 * entity ("certificate of incorporation" for a corporation, an LLC's formation/registration number,
 * a nonprofit's registration, etc.). With no entity selected yet the generic label is shown. The
 * field is REQUIRED (not optional) for every entity type.
 */
const REG_NUMBER_LABEL: Record<OrgEntityType | "", string> = {
  "": "Registration / incorporation number",
  corporation: "Incorporation number (certificate of incorporation)",
  llc: "LLC formation / registration number",
  partnership: "Partnership registration number",
  nonprofit: "Nonprofit / charity registration number",
  sole_proprietorship: "Business / trade registration number",
  other: "Official registration number",
};

const ENTITY_OPTIONS: ReadonlyArray<{ value: OrgEntityType; label: string }> = [
  { value: "corporation", label: "Corporation" },
  { value: "llc", label: "LLC / equivalent" },
  { value: "partnership", label: "Partnership" },
  { value: "nonprofit", label: "Nonprofit" },
  { value: "sole_proprietorship", label: "Sole proprietorship" },
  { value: "other", label: "Other" },
];

const STATUS_LABEL: Record<OrgVerificationStatus, string> = {
  not_verified: "Not verified",
  verification_pending: "Verification pending",
  verified: "Verified",
  verification_failed: "Verification failed",
  verification_expired: "Verification expired",
  verification_revoked: "Verification revoked",
};

function statusTone(s: OrgVerificationStatus): "safe" | "warn" | "threat" | "neutral" {
  if (s === "verified") return "safe";
  if (s === "verification_pending") return "warn";
  if (s === "verification_failed" || s === "verification_expired" || s === "verification_revoked") return "threat";
  return "neutral";
}

/**
 * The visible label and (redundant) colour tone for the current org credential's assurance badge
 * (org-credential-lifecycle Req 8.1). The four states collapse the verification-status family the
 * same way {@link assuranceFromStatus} does, so the badge reads as unverified / pending / verified /
 * failed regardless of which underlying failure status produced it.
 */
const ASSURANCE_LABEL: Record<OrgAssurance, string> = {
  unverified: "Unverified",
  pending: "Verification pending",
  verified: "Verified",
  failed: "Verification failed",
};

function assuranceTone(a: OrgAssurance): PillTone {
  switch (a) {
    case "verified":
      return "safe";
    case "pending":
      return "warn";
    case "failed":
      return "threat";
    case "unverified":
    default:
      return "neutral";
  }
}

function friendlyError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.kind === "forbidden") return "Only an admin of this team can change the organization.";
    if (error.kind === "waking") return "The control plane is still waking up. Try again in a moment.";
    if (error.kind === "network") return "We could not reach the control plane. Check your connection.";
  }
  return fallback;
}

/**
 * Map a register/reissue failure to the specific outcome message the admin sees (Req 8.3). The BFF
 * encodes the reason in the error code/message: a missing/invalid profile (`invalid_org_profile`), a
 * concurrent transition (`org_credential_contended`), or an AIS/VCI/commit failure
 * (`issuance_unavailable`). On any of these the prior credential (if any) is left intact (Req 2.5),
 * so the message is specific and reassures that nothing partial was written.
 */
function registerError(error: unknown, hadCurrentCredential: boolean): string {
  const intact = hadCurrentCredential ? " Your current credential was left unchanged." : "";
  if (error instanceof ApiError) {
    if (error.code === "invalid_org_profile" || error.status === 400) {
      return "The organization registration is incomplete or invalid. Fill in the required fields and try again." + intact;
    }
    if (error.code === "org_credential_contended" || error.status === 409) {
      return "Another credential change is in progress for this organization. Please try again in a moment." + intact;
    }
    if (error.code === "issuance_unavailable" || error.status === 503) {
      return "The credential issuance service is temporarily unavailable." + intact + " Please try again.";
    }
    if (error.kind === "forbidden") return "Only an admin of this team can register the organization credential.";
    if (error.kind === "waking") return "The control plane is still waking up. Try again in a moment." + intact;
    if (error.kind === "network") return "We could not reach the control plane. Check your connection." + intact;
  }
  return "Could not register the organization credential." + intact;
}

export function OrgSettings(props: { canEdit: boolean }): ReactNode {
  const queryClient = useQueryClient();
  const orgQuery = useQuery({ queryKey: ORG_KEY, queryFn: ({ signal }) => getOrganization(signal), staleTime: 15_000, retry: 1 });

  if (orgQuery.isPending) {
    return (
      <article className="oa-account__card" aria-labelledby="oa-org">
        <h2 className="oa-account__card-title" id="oa-org">Organization</h2>
        <div className="oa-account__state" role="status" aria-live="polite">Loading organization…</div>
      </article>
    );
  }
  if (orgQuery.isError || orgQuery.data === null) {
    return (
      <article className="oa-account__card" aria-labelledby="oa-org">
        <h2 className="oa-account__card-title" id="oa-org">Organization</h2>
        <div className="oa-account__feedback" data-kind="error" role="alert">
          {orgQuery.isError ? friendlyError(orgQuery.error, "Could not load the organization.") : "No organization found."}
        </div>
      </article>
    );
  }

  return (
    <OrgSettingsBody
      org={orgQuery.data}
      canEdit={props.canEdit}
      onChanged={() => queryClient.invalidateQueries({ queryKey: ORG_KEY })}
    />
  );
}

function OrgSettingsBody(props: { org: Organization; canEdit: boolean; onChanged: () => void | Promise<void> }): ReactNode {
  const { org } = props;
  const canApply =
    org.verificationStatus === "not_verified" ||
    org.verificationStatus === "verification_failed" ||
    org.verificationStatus === "verification_expired";
  const isPending = org.verificationStatus === "verification_pending";

  // Verification can only be applied for once the organization-registration profile is COMPLETE.
  // Required = every registration field EXCEPT the ones marked optional (registration number). The
  // check is against the SAVED org record (what an application would be judged on), not unsaved edits.
  const REQUIRED_FIELDS: ReadonlyArray<{ key: keyof Organization; label: string }> = [
    { key: "legalName", label: "Legal organization name" },
    { key: "displayName", label: "Organization display name" },
    { key: "entityType", label: "Entity type" },
    { key: "country", label: "Country of registration" },
    { key: "jurisdiction", label: "State / province / jurisdiction" },
    { key: "registrationNumber", label: "Registration / incorporation number" },
    { key: "registeredAddress", label: "Registered business address" },
    { key: "website", label: "Organization website" },
    { key: "businessEmail", label: "Business email" },
    { key: "businessPhone", label: "Business phone" },
    { key: "industry", label: "Industry / business category" },
  ];
  const missingFields = REQUIRED_FIELDS.filter(({ key }) => {
    const v = org[key];
    return v === null || v === undefined || (typeof v === "string" && v.trim() === "");
  }).map((f) => f.label);
  const profileComplete = missingFields.length === 0;
  const missingTooltip = profileComplete
    ? undefined
    : `Complete the organization registration first. Missing: ${missingFields.join(", ")}.`;

  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The per-field GLEIF match report from the last apply (shown on a mismatch so the admin sees
  // exactly what to fix), and the mismatch summary.
  const [report, setReport] = useState<readonly VerificationFieldLine[]>([]);
  const [mismatchSummary, setMismatchSummary] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<VerificationDebug | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  // Supplementary multi-registry corroboration (national registries, conflicts, coverage gaps).
  // GLEIF stays authoritative; this is shown alongside the result for transparency.
  const [corroboration, setCorroboration] = useState<VerificationCorroboration | null>(null);

  const alreadyVerified = org.verificationStatus === "verified";

  const apply = useCallback(async () => {
    setError(null); setBanner(null); setMismatchSummary(null); setBusy(true);
    try {
      const result = await applyForOrgVerification();
      setReport(result.report);
      setShowDebug(false);
      setDebugInfo(result.status === "verification_failed" ? result.debug : null);
      setCorroboration(result.corroboration);
      if (result.status === "verified") {
        setBanner(
          result.reverify
            ? "Re-verified against GLEIF. A new organizational credential was issued and the previous one revoked."
            : "Verified against GLEIF. An organizational credential has been issued.",
        );
      } else {
        setMismatchSummary(result.summary);
      }
      await props.onChanged();
    } catch (e) {
      setError(friendlyError(e, "Could not reach the GLEIF verification service. Please try again."));
    } finally { setBusy(false); }
  }, [props]);

  const cancel = useCallback(async () => {
    setError(null); setBusy(true);
    try {
      await cancelOrgVerification();
      setBanner("Verification request cancelled. You can edit the details and apply again.");
      await props.onChanged();
    } catch (e) {
      setError(friendlyError(e, "Could not cancel the verification request."));
    } finally { setBusy(false); }
  }, [props]);

  return (
    <>
      <OrgProfileForm org={org} canEdit={props.canEdit && !isPending} lockedReason={isPending ? "Cancel the pending verification request to edit the organization." : null} onChanged={props.onChanged} />

      <article className="oa-account__card" aria-labelledby="oa-org-verif">
        <h2 className="oa-account__card-title" id="oa-org-verif">Verification</h2>
        <dl className="oa-account__facts">
          <div className="oa-account__fact">
            <dt>Status</dt>
            <dd><StatusPill tone={statusTone(org.verificationStatus)}>{STATUS_LABEL[org.verificationStatus]}</StatusPill></dd>
          </div>
          {/* The current org credential's assurance badge (Req 8.1): unverified / pending / verified
              / failed, shown only once a credential has been issued (none → nothing to assure). */}
          {org.credentialId !== null && (
            <div className="oa-account__fact">
              <dt>Credential assurance</dt>
              <dd><StatusPill tone={assuranceTone(org.assurance)}>{ASSURANCE_LABEL[org.assurance]}</StatusPill></dd>
            </div>
          )}
          <div className="oa-account__fact">
            <dt>Organization id</dt>
            <dd><code className="oa-account__ref">{org.organizationId}</code></dd>
          </div>
          {org.verificationRequestedAt !== null && (
            <div className="oa-account__fact">
              <dt>Applied</dt>
              <dd>{formatDateTime(org.verificationRequestedAt)}</dd>
            </div>
          )}
          {/* The credential id is public VC reference data (never a secret; Req 8.4), rendered in the
              short/masked head…tail form only (Req 8.1). No copy affordance and no signing-secret
              field is ever exposed. */}
          {org.credentialIdShort !== null && (
            <div className="oa-account__fact">
              <dt>Credential id</dt>
              <dd><code className="oa-account__ref" title="Organization credential reference (public verifiable data)">{org.credentialIdShort}</code></dd>
            </div>
          )}
        </dl>
        <p className="oa-account__card-note">
          Supplying registration details does not make an organization verified. Verification checks
          your registration against an authoritative registry and is performed separately.
        </p>
        {banner !== null && <p className="oa-account__feedback" data-kind="ok" role="status">{banner}</p>}
        {error !== null && <p className="oa-account__feedback" data-kind="error" role="alert">{error}</p>}

        {/* Apply (first-time / after a failure). The button greys out and shows a rotating spinner
            while GLEIF is checked; it is disabled until the registration profile is complete. */}
        {props.canEdit && canApply && (
          <>
            <div className="oa-account__btn-row">
              <span
                className="oa-account__btn-tooltip-wrap"
                {...(missingTooltip !== undefined ? { title: missingTooltip } : {})}
              >
                <button
                  type="button"
                  className="oa-account__btn"
                  disabled={busy || !profileComplete}
                  aria-describedby={profileComplete ? undefined : "oa-verif-missing"}
                  onClick={() => void apply()}
                >
                  {busy && <span className="oa-spinner" aria-hidden="true" />}
                  {busy ? "Verifying with GLEIF\u2026" : "Apply for Verification"}
                </button>
              </span>
            </div>
            {!profileComplete && (
              <p id="oa-verif-missing" className="oa-account__field-hint" role="note">
                Complete the organization registration to apply. Missing: {missingFields.join(", ")}.
              </p>
            )}
          </>
        )}

        {/* Verified → a verified badge and a Re-verify button carrying the revocation warning. */}
        {alreadyVerified && (
          <>
            <p className="oa-account__verified-badge" role="status">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
              Verified against GLEIF
            </p>
            <p className="oa-account__card-note">
              <strong>Re-verifying revokes the current organizational credential.</strong> Agent
              identities issued under the old credential are automatically revoked when it is
              replaced. Only re-verify after updating the organization registration details.
            </p>
            {props.canEdit && (
              <div className="oa-account__btn-row">
                <button
                  type="button"
                  className="oa-account__btn oa-account__btn--danger"
                  disabled={busy}
                  onClick={() => void apply()}
                >
                  {busy && <span className="oa-spinner" aria-hidden="true" />}
                  {busy ? "Re-verifying with GLEIF\u2026" : "Re-verify (revokes current credential)"}
                </button>
              </div>
            )}
          </>
        )}

        {/* A mismatch report: show each field so the admin can see what matched and what to fix. */}
        {mismatchSummary !== null && (
          <div className="oa-account__feedback" data-kind="error" role="alert">
            <p style={{ margin: "0 0 8px" }}>{mismatchSummary}</p>
            {report.length > 0 && (
              <ul className="oa-verif-report">
                {report.map((l) => (
                  <li key={l.field} data-status={l.status}>
                    <span className="oa-verif-report__field">{l.label}</span>
                    <span className="oa-verif-report__verdict">
                      {l.status === "match" ? "\u2713 matches" : l.status === "missing" ? "\u2014 not provided" : `\u2717 you: "${l.found ? l.expected : l.expected}" \u2192 GLEIF: "${l.found}"`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <p className="oa-account__field-hint" style={{ marginTop: 8 }}>
              Edit the highlighted organization registration fields above to match your official
              registration, then apply again.
            </p>

            {/* Debug toggle, pinned to the bottom-right of the error. Expands to show the exact GLEIF
                request + response and which fields matched, for an admin diagnosing a mismatch. */}
            {debugInfo !== null && (
              <>
                <div className="oa-verif-debug-bar">
                  <button
                    type="button"
                    className="oa-verif-debug-toggle"
                    aria-expanded={showDebug}
                    onClick={() => setShowDebug((v) => !v)}
                  >
                    {showDebug ? "Hide debug" : "Debug"}
                  </button>
                </div>
                {showDebug && (
                  <div className="oa-verif-debug">
                    <h4 className="oa-verif-debug__h">Field comparison</h4>
                    <ul className="oa-verif-report">
                      {report.map((l) => (
                        <li key={`dbg-${l.field}`} data-status={l.status}>
                          <span className="oa-verif-report__field">{l.field}</span>
                          <span className="oa-verif-report__verdict">
                            {l.status === "match" ? "✓ matched" : l.status === "missing" ? "— missing" : "✗ did not match"}
                            {"  "}| sent: {JSON.stringify(l.expected)} | gleif: {JSON.stringify(l.found)}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <h4 className="oa-verif-debug__h">GLEIF request</h4>
                    <pre className="oa-verif-debug__pre">{debugInfo.request.method} {debugInfo.request.url}</pre>
                    <h4 className="oa-verif-debug__h">Request headers</h4>
                    <pre className="oa-verif-debug__pre">{JSON.stringify(debugInfo.request.headers, null, 2)}</pre>
                    <h4 className="oa-verif-debug__h">Request fields (compared)</h4>
                    <pre className="oa-verif-debug__pre">{JSON.stringify(debugInfo.request.org, null, 2)}</pre>

                    <h4 className="oa-verif-debug__h">
                      GLEIF response{" "}
                      <span className="oa-verif-debug__meta">
                        (HTTP {debugInfo.response.status}; matched: {String(debugInfo.response.matched)}; candidates: {debugInfo.response.totalCandidates})
                      </span>
                    </h4>
                    <h4 className="oa-verif-debug__h">Response headers</h4>
                    <pre className="oa-verif-debug__pre">{JSON.stringify(debugInfo.response.headers, null, 2)}</pre>
                    <h4 className="oa-verif-debug__h">Response metadata</h4>
                    <pre className="oa-verif-debug__pre">{JSON.stringify(debugInfo.response.meta, null, 2)}</pre>
                    <h4 className="oa-verif-debug__h">Matched record</h4>
                    <pre className="oa-verif-debug__pre">{JSON.stringify(debugInfo.response.record, null, 2)}</pre>
                    {corroboration !== null && (
                      <>
                        <h4 className="oa-verif-debug__h">Multi-registry corroboration (supplementary)</h4>
                        <pre className="oa-verif-debug__pre">{JSON.stringify(corroboration, null, 2)}</pre>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {corroboration !== null && (corroboration.matches.length > 0 || corroboration.conflicts.length > 0 || corroboration.coverageGaps.length > 0) && (
          <div className="oa-verif-corroboration" role="group" aria-label="Multi-registry corroboration">
            <h4 className="oa-verif-corroboration__h">Corroborating registries</h4>
            <p className="oa-account__card-note">
              GLEIF is authoritative for verification. These additional public registries were checked
              for the same organization and are shown for transparency.
            </p>
            {corroboration.matches.length > 0 && (
              <ul className="oa-verif-corroboration__list">
                {corroboration.matches.map((m, i) => (
                  <li key={`${m.provider}-${i}`} className="oa-verif-corroboration__item">
                    <span className="oa-verif-corroboration__provider">{m.provider}</span>
                    <span className="oa-verif-corroboration__name">{m.legalName}</span>
                    <span className={`oa-verif-corroboration__status oa-verif-corroboration__status--${m.status}`}>{m.status}</span>
                    {m.sourceUrl !== undefined && (
                      <a className="oa-verif-corroboration__link" href={m.sourceUrl} target="_blank" rel="noreferrer noopener">source</a>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {corroboration.conflicts.length > 0 && (
              <p className="oa-verif-corroboration__conflict" role="alert">
                Conflicting records across registries: {corroboration.conflicts.join("; ")}
              </p>
            )}
            {corroboration.coverageGaps.length > 0 && (
              <p className="oa-account__card-note">
                No keyless public registry covers: {corroboration.coverageGaps.map((g) => `${g.provider} (${g.reason})`).join(", ")}.
              </p>
            )}
          </div>
        )}

        {isPending && (
          <>
            <p className="oa-account__card-note">
              Your verification application is pending. If you applied by mistake or need to change
              details, cancel the request, edit the organization, and apply again.
            </p>
            {props.canEdit && (
              <div className="oa-account__btn-row">
                <button type="button" className="oa-account__btn oa-account__btn--ghost" disabled={busy} onClick={() => void cancel()}>
                  {busy ? "Cancelling\u2026" : "Cancel verification request"}
                </button>
              </div>
            )}
          </>
        )}
      </article>

    </>
  );
}

interface FormState {
  legalName: string; displayName: string; entityType: OrgEntityType | "";
  country: string; jurisdiction: string; registrationNumber: string; registeredAddress: string;
  website: string; businessEmail: string; businessPhone: string; industry: string;
}

function OrgProfileForm(props: { org: Organization; canEdit: boolean; lockedReason: string | null; onChanged: () => void | Promise<void> }): ReactNode {
  const { org } = props;
  const seed = useMemo<FormState>(() => ({
    legalName: org.legalName ?? "", displayName: org.displayName ?? "", entityType: org.entityType ?? "",
    country: org.country ?? "", jurisdiction: org.jurisdiction ?? "", registrationNumber: org.registrationNumber ?? "",
    registeredAddress: org.registeredAddress ?? "", website: org.website ?? "", businessEmail: org.businessEmail ?? "",
    businessPhone: formatPhoneForDisplay(org.businessPhone ?? "", org.country ?? ""), industry: org.industry ?? "",
  }), [org]);
  const [f, setF] = useState<FormState>(seed);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The pre-action confirmation dialog (Req 1.2, 1.3): opened on submit so the admin acknowledges the
  // reissue (revoke-and-issue) or first issuance wording BEFORE the credential is minted.
  const [confirmOpen, setConfirmOpen] = useState(false);
  useEffect(() => setF(seed), [seed]);

  // A current org credential already exists → registering again revokes it and issues a fresh one
  // (Req 1.2 / 3.1); none exists → the first issuance (Req 1.3). Drives the pre-action and outcome
  // wording.
  const hasCurrentCredential = org.credentialId !== null;

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setF((p) => ({ ...p, [k]: v }));

  // Field errors (inline). Website/email/phone validated; required: legal name is recommended but we
  // only hard-require it when applying — here nothing is hard-required to allow partial saves.
  const errors = useMemo(() => ({
    website: validateUrl(f.website),
    businessEmail: validateEmail(f.businessEmail, "Business email"),
    businessPhone: validatePhoneForCountry(f.businessPhone, f.country, "Business phone"),
    // The four fields GLEIF verification uses are MANDATORY: legal name, registration number,
    // jurisdiction and country of registration.
    legalName: validateRequiredText(f.legalName, "Legal organization name", 256),
    displayName: validateOptionalText(f.displayName, "Organization display name"),
    registrationNumber: validateRequiredText(f.registrationNumber, REG_NUMBER_LABEL[f.entityType], 128),
    country: f.country.trim() === "" ? "Country of registration is required." : null,
    jurisdiction: f.jurisdiction.trim() === "" ? "Jurisdiction is required." : null,
  }), [f.website, f.businessEmail, f.businessPhone, f.country, f.jurisdiction, f.legalName, f.displayName, f.registrationNumber, f.entityType]);
  const hasErrors = Object.values(errors).some((e) => e !== null);

  // Country-dependent subdivision control.
  const subs = subdivisionsFor(f.country === "" ? null : f.country);

  const legalId = useId(); const displayId = useId(); const entityId = useId(); const countryId = useId();
  const jurisId = useId(); const regNumId = useId(); const addrId = useId(); const webId = useId();
  const bemailId = useId(); const bphoneId = useId(); const indId = useId();

  // Submit opens the pre-action confirmation rather than acting immediately (Req 1.2): the admin must
  // acknowledge the issue/reissue wording first. Field validation is enforced here so the dialog only
  // ever appears for a complete, valid profile.
  const requestRegister = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setError(null); setBanner(null);
    if (hasErrors) { setError("Please fix the highlighted fields before registering."); return; }
    setConfirmOpen(true);
  }, [hasErrors]);

  // Perform the registration after the admin confirms: persist the profile (the authoritative claims
  // core projects, Req 7.7), then register the credential with an empty body. The triggering control
  // is disabled while this runs (Req 8.2). The outcome toast states issued vs reissued-with-old-
  // revoked per Req 8.3 / 1.2–1.3.
  const confirmRegister = useCallback(async () => {
    setError(null); setBanner(null); setConfirmOpen(false); setBusy(true);
    try {
      await updateOrganization({
        legalName: f.legalName, displayName: f.displayName, entityType: f.entityType, country: f.country,
        jurisdiction: f.jurisdiction, registrationNumber: f.registrationNumber, registeredAddress: f.registeredAddress,
        website: f.website, businessEmail: f.businessEmail,
        businessPhone: normalizePhoneForStorage(f.businessPhone, f.country), industry: f.industry,
      });
      const result = await registerOrgCredential();
      setBanner(
        result.reissued
          ? "Organization credential reissued. The previous credential was revoked and a new unverified credential issued."
          : "Organization credential issued. A new unverified credential was created for your organization.",
      );
      await props.onChanged();
    } catch (err) {
      setError(registerError(err, hasCurrentCredential));
    } finally { setBusy(false); }
  }, [f, hasCurrentCredential, props]);

  const ro = !props.canEdit;

  return (
    <article className="oa-account__card" aria-labelledby="oa-org-profile">
      <h2 className="oa-account__card-title" id="oa-org-profile">Organization registration</h2>
      <p className="oa-account__card-note">
        {props.lockedReason ?? (ro ? "Registration details for this organization." : "Registration details. A registration number is optional (some entity types do not have one).")}
      </p>
      <form className="oa-account__form" onSubmit={requestRegister}>
        <TextField id={legalId} label="Legal organization name" value={f.legalName} onChange={(v) => set("legalName", v)} ro={ro} error={errors.legalName} required />
        <TextField id={displayId} label="Organization display name" value={f.displayName} onChange={(v) => set("displayName", v)} ro={ro} error={errors.displayName} />
        <div className="oa-account__row">
          <label className="oa-account__label" htmlFor={entityId}>Entity type</label>
          <select id={entityId} className="oa-account__select" value={f.entityType} disabled={ro} onChange={(e) => set("entityType", e.target.value as OrgEntityType | "")}>
            <option value="">Not specified</option>
            {ENTITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="oa-account__row">
          <label className="oa-account__label" htmlFor={countryId}>Country of registration<span className="oa-account__req" aria-hidden="true">*</span></label>
          <select
            id={countryId}
            className="oa-account__select"
            value={f.country}
            disabled={ro}
            aria-invalid={errors.country ? "true" : undefined}
            onChange={(e) => {
              const nextCountry = e.target.value;
              setF((p) => ({
                ...p,
                country: nextCountry,
                jurisdiction: "",
                // Re-group the already-entered phone under the newly selected country.
                businessPhone: formatPhoneForDisplay(p.businessPhone, nextCountry),
              }));
            }}
          >
            <option value="">Not specified</option>
            {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
          {errors.country && <span className="oa-account__field-error" role="alert">{errors.country}</span>}
        </div>
        <div className="oa-account__row">
          <label className="oa-account__label" htmlFor={jurisId}>State / province / territory / jurisdiction<span className="oa-account__req" aria-hidden="true">*</span></label>
          {subs !== null ? (
            <select id={jurisId} className="oa-account__select" value={f.jurisdiction} disabled={ro} aria-invalid={errors.jurisdiction ? "true" : undefined} onChange={(e) => set("jurisdiction", e.target.value)}>
              <option value="">Not specified</option>
              {subs.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <input id={jurisId} className="oa-account__input" type="text" value={f.jurisdiction} disabled={ro} aria-invalid={errors.jurisdiction ? "true" : undefined} onChange={(e) => set("jurisdiction", e.target.value)} placeholder={f.country === "" ? "Select a country first" : "Enter the jurisdiction"} />
          )}
          {errors.jurisdiction && <span className="oa-account__field-error" role="alert">{errors.jurisdiction}</span>}
        </div>
        <TextField id={regNumId} label={REG_NUMBER_LABEL[f.entityType]} value={f.registrationNumber} onChange={(v) => set("registrationNumber", v)} ro={ro} error={errors.registrationNumber} required />
        <TextField id={addrId} label="Registered business address" value={f.registeredAddress} onChange={(v) => set("registeredAddress", v)} ro={ro} />
        <TextField id={webId} label="Organization website" value={f.website} onChange={(v) => set("website", formatWebsiteSmart(v))} ro={ro} type="url" placeholder="https://example.com" error={errors.website} />
        <TextField id={bemailId} label="Business email" value={f.businessEmail} onChange={(v) => set("businessEmail", v)} ro={ro} type="email" error={errors.businessEmail} />
        <TextField
          id={bphoneId}
          label="Business phone"
          value={f.businessPhone}
          onChange={(v) => set("businessPhone", formatPhoneForDisplay(v, f.country))}
          ro={ro}
          type="tel"
          error={errors.businessPhone}
          placeholder={examplePhonePlaceholder(f.country)}
        />
        <div className="oa-account__row">
          <label className="oa-account__label" htmlFor={indId}>Industry / business category</label>
          <select id={indId} className="oa-account__select" value={f.industry} disabled={ro} onChange={(e) => set("industry", e.target.value)}>
            <option value="">Not specified</option>
            {INDUSTRY_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        {banner !== null && <p className="oa-account__feedback" data-kind="ok" role="status">{banner}</p>}
        {error !== null && <p className="oa-account__feedback" data-kind="error" role="alert">{error}</p>}
        {/* The primary org-registration action (Req 1.1). It is rendered only for an admin (the whole
            Members screen is admin-gated, and `ro` hides the control for a non-editor; Req 1.4) and
            is disabled while a registration is in flight to prevent duplicate submissions (Req 8.2).
            No private key or signing-secret field is rendered anywhere — the credential is public VC
            data (Req 8.4). */}
        {!ro && (
          <div className="oa-account__btn-row">
            <button type="submit" className="oa-account__btn" disabled={busy || hasErrors}>
              {busy && <span className="oa-spinner" aria-hidden="true" />}
              {busy ? "Registering credential\u2026" : "Register organization credential"}
            </button>
          </div>
        )}
      </form>

      {/* Pre-action confirmation (Req 1.2 / 1.3): reissue wording when a current credential exists,
          plain issue wording when none does. The admin must confirm before any mint/revoke runs. */}
      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        role="alertdialog"
        title={hasCurrentCredential ? "Reissue organization credential?" : "Register organization credential?"}
        description={
          hasCurrentCredential
            ? "The existing OrganizationSponsorCredential will be revoked and a new one issued. The new credential starts unverified and must be re-verified."
            : "A new OrganizationSponsorCredential will be issued for your organization. It starts unverified until you apply for verification."
        }
        footer={
          <>
            <button type="button" className="oa-account__btn oa-account__btn--ghost" onClick={() => setConfirmOpen(false)} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="oa-account__btn" onClick={() => void confirmRegister()} disabled={busy}>
              {hasCurrentCredential ? "Revoke and reissue" : "Issue credential"}
            </button>
          </>
        }
      >
        <p className="oa-account__card-note">
          The credential's claims come from the organization registration saved above. No private key
          or signing secret is ever shown or stored in your browser.
        </p>
      </Modal>
    </article>
  );
}

function TextField(props: {
  id: string; label: string; value: string; onChange: (v: string) => void; ro: boolean;
  type?: "text" | "email" | "url" | "tel"; placeholder?: string; error?: string | null; required?: boolean;
}): ReactNode {
  const hasError = props.error != null;
  const errId = `${props.id}-err`;
  return (
    <div className="oa-account__row">
      <label className="oa-account__label" htmlFor={props.id}>
        {props.label}
        {props.required === true && <span className="oa-account__req" aria-hidden="true">*</span>}
      </label>
      <input
        id={props.id}
        className="oa-account__input"
        type={props.type ?? "text"}
        value={props.value}
        disabled={props.ro}
        aria-invalid={hasError ? "true" : undefined}
        aria-describedby={hasError ? errId : undefined}
        {...(props.placeholder !== undefined ? { placeholder: props.placeholder } : {})}
        onChange={(e) => props.onChange(e.target.value)}
      />
      {hasError && <span id={errId} className="oa-account__field-error" role="alert">{props.error}</span>}
    </div>
  );
}
